import type { CustomerStatusSummary } from 'services/customerStatusService';
import type { BaseFlags } from 'services/baseTerritoryService';

export type BlockReasonId =
  | 'OVERDUE_PAYMENT'
  | 'PAYMENT_TERM'
  | 'IRREGULAR_TAXPAYER'
  | 'IRREGULAR_BASE';

export type BlockReason = {
  id: BlockReasonId;
  bases?: string[];
};

export type UnverifiedBlock =
  | 'fiscal'
  | 'registration'
  | 'financial'
  | 'territory';

export type ResolveBlockReasonsInput = {
  summary?: CustomerStatusSummary;
  flagsByBase?: Record<string, BaseFlags>;
  failedBases?: string[];
  summaryFailed?: boolean;
};

export type ResolveBlockReasonsOutput = {
  reasons: BlockReason[];
  unverified: UnverifiedBlock[];
};

const normalize = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    // eslint-disable-next-line no-control-regex
    .replace(/[̀-ͯ]/g, '');

export const isCashPaymentTerm = (paymentTerm?: string): boolean => {
  if (!paymentTerm) return false;

  const term = normalize(paymentTerm);
  return term.includes('a vista') || term === 'avista';
};

export const isIrregularOracleStatus = (status?: string): boolean => {
  if (status === undefined || status === null || status === '') return false;
  return normalize(status) !== 'ativa';
};

export const isIrregularBase = (flags?: BaseFlags): boolean => {
  if (!flags) return false;

  return (
    flags.active === false ||
    flags.gasReleased === false ||
    isIrregularOracleStatus(flags.oracleStatus)
  );
};

export const getBaseIrregularityReasons = (flags?: BaseFlags): string[] => {
  if (!flags) return [];

  const reasons: string[] = [];
  if (flags.active === false) reasons.push('Central inativa');
  if (flags.gasReleased === false) reasons.push('Gás não liberado');
  if (isIrregularOracleStatus(flags.oracleStatus)) {
    reasons.push(`Status: ${flags.oracleStatus}`);
  }

  return reasons;
};

export const resolveBlockReasons = ({
  summary,
  flagsByBase = {},
  failedBases = [],
  summaryFailed = false,
}: ResolveBlockReasonsInput): ResolveBlockReasonsOutput => {
  const reasons: BlockReason[] = [];
  const unverified: UnverifiedBlock[] = [];

  const hasOverdue = summary?.financial?.hasOverdueBankSlips === true;

  if (summaryFailed || !summary?.financial) {
    unverified.push('financial');
  } else if (hasOverdue) {
    reasons.push({ id: 'OVERDUE_PAYMENT' });
  }

  if (summaryFailed || !summary?.registration) {
    unverified.push('registration');
  }

  if (
    !summaryFailed &&
    isCashPaymentTerm(summary?.paymentTerm) &&
    !hasOverdue
  ) {
    reasons.push({ id: 'PAYMENT_TERM' });
  }

  if (summaryFailed || !summary?.fiscal) {
    unverified.push('fiscal');
  }

  const fiscalIrregular = summary?.fiscal?.regular === false;
  const registrationMismatch =
    summary?.registration?.stateRegistration?.match === false;

  if (!summaryFailed && (fiscalIrregular || registrationMismatch)) {
    reasons.push({ id: 'IRREGULAR_TAXPAYER' });
  }

  const irregularBases = Object.keys(flagsByBase).filter((baseCode) =>
    isIrregularBase(flagsByBase[baseCode])
  );

  if (irregularBases.length > 0) {
    reasons.push({ id: 'IRREGULAR_BASE', bases: irregularBases });
  }

  if (failedBases.length > 0) {
    unverified.push('territory');
  }

  return { reasons, unverified };
};

export const hasReason = (reasons: BlockReason[], id: BlockReasonId): boolean =>
  reasons.some((reason) => reason.id === id);

export const getReason = (
  reasons: BlockReason[],
  id: BlockReasonId
): BlockReason | undefined => reasons.find((reason) => reason.id === id);

export type BlockDestination = 'none' | 'bases' | 'pendencias';

export const resolveBlockDestination = (
  reasons: BlockReason[]
): BlockDestination => {
  if (reasons.length === 0) return 'none';
  if (reasons.length === 1 && reasons[0].id === 'IRREGULAR_BASE')
    return 'bases';
  return 'pendencias';
};

export const REASON_TAG_LABEL: Record<BlockReasonId, string> = {
  OVERDUE_PAYMENT: 'pagamentos pendentes',
  PAYMENT_TERM: 'condicao de pagamento',
  IRREGULAR_TAXPAYER: 'regularizar cnpj',
  IRREGULAR_BASE: 'central irregular',
};

export const buildReasonsTagLabel = (reasons: BlockReason[]): string =>
  reasons.map((reason) => REASON_TAG_LABEL[reason.id]).join('|');
